import type { ReactNode } from "react";
import { Card } from "../primitives/card";
import { cn } from "../primitives/utils";

/**
 * StatCard — the shared headline stat tile used by the list pages
 * (sales orders + work portfolio). Label above a large monochrome value.
 *
 * Values are intentionally **not** colour-coded: the value stays the
 * default foreground everywhere. (Earlier the sales cards leaned on
 * `var(--color-success)` / `var(--color-warning)` for this, which were
 * undefined at the time and so silently rendered black — making
 * "monochrome" look accidental. Both tokens are now defined in
 * `packages/shared-core/src/tokens/source.ts`; this component still opts
 * out of colour-coding deliberately, to make the intent explicit and
 * consistent rather than relying on every caller remembering not to.)
 *
 * Pass `onClick` to make the tile an interactive filter button (used by the
 * work portfolio's RAG buckets); the active tile gets a primary ring.
 * Without `onClick` it renders as a static tile (sales orders).
 *
 * Pass `icon` to show a small status glyph beside the label — the work
 * portfolio uses this so each RAG card carries the same icon as its rows,
 * making the card↔row mapping obvious.
 *
 * Pass `detail` for a short line under the value (counts, a comparison, a
 * status); month end's tile tabs use it.
 *
 * Pass `className` to adjust sizing/spacing for a specific tile row (e.g.
 * a shared min-height so a wrapping label doesn't make one tile taller than
 * its neighbours) without changing the default look of every other caller.
 */
export function StatCard({
  label,
  value,
  detail,
  active = false,
  onClick,
  icon,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  active?: boolean;
  onClick?: () => void;
  icon?: ReactNode;
  className?: string;
}) {
  const card = (
    <Card
      className={cn(
        "p-4",
        onClick && "transition-colors hover:bg-muted/50",
        active && "ring-2 ring-primary",
        className,
      )}
    >
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {detail && (
          <div className="text-muted-foreground mt-1 text-xs">{detail}</div>
        )}
      </div>
    </Card>
  );

  if (!onClick) return card;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="text-left w-full h-full"
    >
      {card}
    </button>
  );
}
